import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "@/components/Container";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, X, Download, Save, Loader2, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const TYPES = ["Detached house","Semi-detached house","Terraced house","End terrace","Bungalow","Flat","Maisonette","Cottage","Townhouse","Mews house","Commercial","Mixed-use","Land","Plot","Farm","Barn","Block of flats","HMO","Conversion","Development site","Office","Retail unit","Warehouse","Industrial","Hotel/guesthouse"];

// Editorial score bands — mirrors backend `scoring.band()`
function scoreBand(score) {
  const s = Number(score) || 0;
  if (s >= 90) return { label: "Exceptional", color: "text-emerald-300 border-emerald-500/40" };
  if (s >= 75) return { label: "Strong",      color: "text-gold border-gold/40" };
  if (s >= 60) return { label: "Review",      color: "text-bronze border-bronze/40" };
  if (s >= 40) return { label: "Weak",        color: "text-ivory/50 border-ivory/20" };
  return { label: "Avoid", color: "text-ruby-light border-ruby/40" };
}
function riskBand(score) {
  const s = Number(score) || 0;
  if (s >= 60) return "text-ruby-light";
  if (s >= 40) return "text-bronze";
  if (s >= 20) return "text-ivory/70";
  return "text-emerald-300";
}

export default function PropertyDatabase() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", city: "", postcode: "", property_type: "Terraced house", asking_price: "", bedrooms: "", description: "" });

  // URL import flow
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [savingPreview, setSavingPreview] = useState(false);

  const load = () => {
    const q = search ? `?search=${encodeURIComponent(search)}&limit=${pageSize}&offset=${(page-1)*pageSize}` : `?limit=${pageSize}&offset=${(page-1)*pageSize}`;
    api.get(`/properties${q}`).then(r => {
      setItems(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    });
  };
  useEffect(() => { load(); }, [search, page]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/properties", {
        ...form,
        asking_price: form.asking_price ? Number(form.asking_price) : null,
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
      });
      toast.success("Property added"); setShowForm(false);
      setForm({ title: "", city: "", postcode: "", property_type: "Terraced house", asking_price: "", bedrooms: "", description: "" });
      load();
    } catch (er) { toast.error("Failed"); }
  };

  const previewImport = async () => {
    if (!importUrl) return toast.error("Paste a property URL");
    setImporting(true);
    try {
      const { data } = await api.post("/properties/import-url", { url: importUrl, save: false });
      setPreview(data.preview);
      if (data.preview?._warning === "fetch_failed") {
        toast.warning("Site blocked direct fetch — edit the preview manually before saving.");
      } else {
        toast.success("Scraped — review and save below");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Import failed")); }
    setImporting(false);
  };

  const setPreviewField = (k, v) => setPreview((p) => ({ ...p, [k]: v }));

  const savePreview = async () => {
    setSavingPreview(true);
    try {
      const { data } = await api.post("/properties", {
        ...preview,
        asking_price: preview.asking_price ? Number(preview.asking_price) : null,
        bedrooms: preview.bedrooms ? Number(preview.bedrooms) : null,
        bathrooms: preview.bathrooms ? Number(preview.bathrooms) : null,
      });
      toast.success(`Saved · ${data.title}`);
      setPreview(null);
      setImportUrl("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail, "Save failed")); }
    setSavingPreview(false);
  };

  return (
    <Container className="py-8 md:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Deals</p>
          <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Property Database</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              const t = toast.loading("Rescoring all properties…");
              try {
                const { data } = await api.post("/properties/rescore-all");
                toast.success(`Rescored ${data.updated} properties`, { id: t });
                load();
              } catch { toast.error("Rescore failed", { id: t }); }
            }}
            className="btn-outline"
            data-testid="rescore-all-btn"
          >
            <Sparkles size={13} /> Rescore all
          </button>
          <button onClick={() => setShowForm(s => !s)} className="btn-ruby" data-testid={ADMIN.createProperty}>
            {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Property"}
          </button>
        </div>
      </div>

      {/* ────── URL IMPORT ────── */}
      <div className="luxury-card-ruby p-6 mt-6">
        <p className="overline">Import from listing URL</p>
        <p className="text-xs text-ivory/50 mt-1">
          Paste any Rightmove, Zoopla, OnTheMarket, auction or estate-agent listing URL — we&apos;ll fetch and pre-fill the form. Then review &amp; save.
        </p>
        <div className="mt-4 flex flex-col md:flex-row gap-3">
          <input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://www.rightmove.co.uk/properties/123456..."
            data-testid="import-url-input"
            className="flex-1 bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none"
          />
          <button
            onClick={previewImport}
            disabled={importing}
            data-testid="import-url-fetch"
            className="btn-ruby justify-center disabled:opacity-50"
          >
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            {importing ? "Scraping…" : "Fetch & Preview"}
          </button>
        </div>
      </div>

      {/* ────── PREVIEW (after import) ────── */}
      {preview && (
        <div className="luxury-card p-6 mt-4" data-testid="import-preview">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <p className="overline">Preview · edit any field before saving</p>
              <p className="text-xs text-ivory/50 mt-1">{preview.source_website || preview.source_url}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPreview(null)} className="btn-outline !py-2 !px-3 !text-[0.65rem]"><X size={12} /> Discard</button>
              <button onClick={savePreview} disabled={savingPreview} data-testid="import-save" className="btn-ruby !py-2 !px-4 !text-[0.7rem] disabled:opacity-50">
                {savingPreview ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save Property
              </button>
            </div>
          </div>
          {preview.public_main_image && (
            <img src={preview.public_main_image} alt="" className="w-full max-h-72 object-cover mb-4" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Title" value={preview.title || ""} onChange={(e) => setPreviewField("title", e.target.value)} />
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="City" value={preview.city || ""} onChange={(e) => setPreviewField("city", e.target.value)} />
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Postcode" value={preview.postcode || ""} onChange={(e) => setPreviewField("postcode", e.target.value)} />
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Asking price" type="number" value={preview.asking_price || ""} onChange={(e) => setPreviewField("asking_price", e.target.value)} />
            <select className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={preview.property_type || ""} onChange={(e) => setPreviewField("property_type", e.target.value)}>
              <option value="">Type…</option>
              {TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Bedrooms" type="number" value={preview.bedrooms || ""} onChange={(e) => setPreviewField("bedrooms", e.target.value)} />
            <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Bathrooms" type="number" value={preview.bathrooms || ""} onChange={(e) => setPreviewField("bathrooms", e.target.value)} />
            <textarea rows={4} className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Description" value={preview.description || ""} onChange={(e) => setPreviewField("description", e.target.value)} />
            {preview.matched_keywords?.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-[0.65rem] uppercase tracking-[0.15em] text-gold">Matched keywords</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {preview.matched_keywords.map(k => <span key={k} className="text-[0.65rem] uppercase tracking-wider border border-gold/40 text-gold px-2 py-1">{k}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="luxury-card p-6 mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Title *" value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="property-title" />
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="City" value={form.city} onChange={e=>setForm({...form, city:e.target.value})} />
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Postcode" value={form.postcode} onChange={e=>setForm({...form, postcode:e.target.value})} />
          <select className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" value={form.property_type} onChange={e=>setForm({...form, property_type:e.target.value})}>
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Asking price (£)" type="number" value={form.asking_price} onChange={e=>setForm({...form, asking_price:e.target.value})} />
          <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Bedrooms" type="number" value={form.bedrooms} onChange={e=>setForm({...form, bedrooms:e.target.value})} />
          <textarea rows={3} className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Description" value={form.description} onChange={e=>setForm({...form, description:e.target.value})} />
          <button className="btn-ruby md:col-span-2 justify-center" data-testid="submit-property">Create Property</button>
        </form>
      )}

      <input
          placeholder="Search by title, city, postcode…"
          value={search} onChange={e => { setPage(1); setSearch(e.target.value); }}
        className="mt-8 w-full md:w-1/2 bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm"
        data-testid="property-search"
      />

      <div className="mt-6 luxury-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-charcoal-700 text-ivory/60 text-[0.65rem] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left p-3"></th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Location</th>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Asking</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Opp.</th>
              <th className="text-right p-3">Risk</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-ivory/40">No properties yet.</td></tr>}
            {items.map(p => {
              const thumb = p.public_main_image || (p.images && p.images[0]) || "";
              const opp = scoreBand(p.opportunity_score);
              return (
                <tr key={p.id} className="border-t border-charcoal-600/60 hover:bg-charcoal-800/40" data-testid={ADMIN.propertyRow(p.id)}>
                  <td className="p-2">
                    {thumb ? (
                      <img src={thumb} alt="" className="w-14 h-10 object-cover" onError={(e)=>{e.target.style.visibility="hidden";}} />
                    ) : (
                      <div className="w-14 h-10 bg-charcoal-800 border border-charcoal-600" />
                    )}
                  </td>
                  <td className="p-3 text-ivory">{p.title}</td>
                  <td className="p-3 text-ivory/70">{p.city} {p.postcode}</td>
                  <td className="p-3 text-ivory/60">{p.property_type}</td>
                  <td className="p-3 text-right text-ivory">£{(p.asking_price || 0).toLocaleString()}</td>
                  <td className="p-3"><span className="text-[0.65rem] uppercase tracking-[0.15em] text-gold">{p.deal_status}</span></td>
                  <td className="p-3 text-right">
                    {p.opportunity_score ? (
                      <span
                        className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[0.7rem] font-mono ${opp.color}`}
                        title={(p.opportunity_reasons || []).join(" · ") || opp.label}
                        data-testid={`opp-score-${p.id}`}
                      >
                        {p.opportunity_score} <span className="text-[0.55rem] uppercase tracking-[0.15em] opacity-70">{opp.label}</span>
                      </span>
                    ) : <span className="text-ivory/30">—</span>}
                  </td>
                  <td className={`p-3 text-right font-mono text-xs ${riskBand(p.risk_score)}`}
                      title={(p.risk_reasons || []).join(" · ") || ""}
                      data-testid={`risk-score-${p.id}`}>
                    {p.risk_score || "—"}
                  </td>
                  <td className="p-3 text-right"><Link to={`/admin/properties/${p.id}`} className="btn-outline !py-1 !px-3 !text-[0.65rem]">Open</Link></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between text-xs text-ivory/50" data-testid="property-pagination">
        <span>{total > 0 ? `${(page-1)*pageSize + 1}–${Math.min(page*pageSize, total)} of ${total}` : "—"}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p-1)}
            className="btn-outline !py-1 !px-2 !text-[0.65rem] disabled:opacity-30" data-testid="prop-page-prev">
            <ChevronLeft size={12} /> Prev
          </button>
          <span className="font-mono text-ivory/70">{page} / {Math.max(1, Math.ceil(total/pageSize))}</span>
          <button disabled={page >= Math.ceil(total/pageSize)} onClick={() => setPage(p => p+1)}
            className="btn-outline !py-1 !px-2 !text-[0.65rem] disabled:opacity-30" data-testid="prop-page-next">
            Next <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </Container>
  );
}
